/**
 * The boot diagnostic.
 *
 * WebGPU is committed, and the fallback is never WebGL2
 * (docs/13-architecture.md). A build that silently limps along on Three's
 * incidental WebGL2 path would be worse than one that stops: it would produce
 * frame numbers for a renderer we do not ship, in the one prototype whose job
 * is to measure the renderer we do. So the requirement is stated plainly at
 * boot, in words, naming what is missing.
 *
 * The same probe doubles as the platform matrix's data collector. B0.1 owes the
 * cheap half of that matrix — a runs/doesn't-run answer per cell
 * (docs/14-roadmap.md) — and the honest way to collect it from browsers and
 * webviews nobody can automate is to have the build report on itself. Hence
 * `environmentReport`, and the copy button beside it.
 */

export interface EnvironmentReport {
  userAgent: string;
  /** `navigator.userAgentData.platform` where available, else best effort. */
  platform: string;
  /** Device pixel ratio, because a matrix cell that ran at 0.5× is not a pass. */
  devicePixelRatio: number;
  crossOriginIsolated: boolean;
  sharedArrayBuffer: boolean;
  webgpu: WebGpuProbe;
}

export type WebGpuProbe =
  | { ok: true; adapter: AdapterSummary }
  | { ok: false; reason: string; detail?: string | undefined };

export interface AdapterSummary {
  vendor: string;
  architecture: string;
  device: string;
  description: string;
  /** A few limits worth having on record when a cell renders but crawls. */
  maxBufferSize: number;
  maxComputeWorkgroupsPerDimension: number;
}

/**
 * Ask the browser for a WebGPU adapter and describe what came back. Failure is
 * a value, not an exception: every cell of the matrix wants an answer, and
 * "threw on requestAdapter" is an answer.
 */
export async function probeWebGpu(): Promise<WebGpuProbe> {
  const gpu = navigator.gpu as GPU | undefined;
  if (!gpu) {
    return {
      ok: false,
      reason: 'This browser does not expose WebGPU (`navigator.gpu` is missing).',
      detail: window.isSecureContext
        ? undefined
        : 'The page is not a secure context, which alone is enough to hide WebGPU. Serve it over https or localhost.',
    };
  }
  let adapter: GPUAdapter | null = null;
  try {
    adapter = await gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch (error) {
    return {
      ok: false,
      reason: 'WebGPU is present but requesting an adapter threw.',
      detail: String(error),
    };
  }
  if (!adapter) {
    return {
      ok: false,
      reason: 'WebGPU is present but no adapter was granted.',
      detail:
        'Usually a driver or GPU-blocklist decision. On Linux this is the common shape of "WebGPU is behind a flag".',
    };
  }
  const info = adapter.info;
  return {
    ok: true,
    adapter: {
      vendor: info?.vendor ?? 'unknown',
      architecture: info?.architecture ?? 'unknown',
      device: info?.device ?? 'unknown',
      description: info?.description ?? 'unknown',
      maxBufferSize: adapter.limits.maxBufferSize,
      maxComputeWorkgroupsPerDimension: adapter.limits.maxComputeWorkgroupsPerDimension,
    },
  };
}

/** Everything a platform-matrix cell wants to record about this run. */
export async function environmentReport(): Promise<EnvironmentReport> {
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  return {
    userAgent: navigator.userAgent,
    platform: uaData?.platform ?? 'unknown',
    devicePixelRatio: window.devicePixelRatio,
    crossOriginIsolated: window.crossOriginIsolated,
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    webgpu: await probeWebGpu(),
  };
}

/**
 * Replace the page with a plain statement of what is missing, plus the report
 * a matrix cell needs. Deliberately DOM and not React: if the stack is broken
 * badly enough to reach here, the fewer moving parts the better.
 */
export function renderBootFailure(host: HTMLElement, report: EnvironmentReport): void {
  const probe = report.webgpu;
  const reason = probe.ok ? 'Unknown boot failure.' : probe.reason;
  const detail = probe.ok ? '' : (probe.detail ?? '');

  host.classList.add('boot-failure');
  host.innerHTML = '';

  const title = document.createElement('h1');
  title.textContent = 'YIELD needs WebGPU';
  const line = document.createElement('p');
  line.textContent = reason;
  const note = document.createElement('p');
  note.className = 'muted';
  note.textContent =
    detail ||
    'YIELD renders on WebGPU and does not support WebGL2. Chrome or Edge 113+, Firefox on a build with WebGPU enabled, or Safari 18+ will run it.';

  const pre = document.createElement('pre');
  pre.textContent = JSON.stringify(report, null, 2);

  const copy = document.createElement('button');
  copy.textContent = 'Copy platform report';
  copy.addEventListener('click', () => {
    void navigator.clipboard?.writeText(JSON.stringify(report, null, 2));
    copy.textContent = 'Copied — paste it into web/platform-matrix.md';
  });

  host.append(title, line, note, copy, pre);
}
